<script setup lang="ts">
/**
 * Homepage teaser for /how-to-prepare-for-an-interview.
 *
 * Four of the guide's points, each linking to its own anchor on the guide, so
 * the homepage passes links to the sections people search for separately
 * (the STAR method, the common questions) and not only to the page top.
 */
const { t } = useI18n()

const GUIDE = '/how-to-prepare-for-an-interview'

const tips = [
  { id: 'posting', to: `${GUIDE}#step-2` },
  { id: 'cv', to: `${GUIDE}#step-3` },
  { id: 'star', to: `${GUIDE}#star-method` },
  { id: 'questions', to: `${GUIDE}#common-questions` },
] as const
</script>

<template>
  <section class="sofax surface-alt">
    <div class="section-space">
      <div class="shell">
        <div class="grid grid-cols-1 items-center gap-12 lg:grid-cols-[.8fr_1.2fr] lg:gap-20">
          <div>
            <div class="eyebrow mb-5">{{ t('homepage.interviewPrep.eyebrow') }}</div>
            <h2 class="mb-5">{{ t('homepage.interviewPrep.title') }}</h2>
            <p class="mb-8 text-lg">{{ t('homepage.interviewPrep.text') }}</p>
            <SofaxBtn :to="GUIDE" :label="t('homepage.interviewPrep.cta')" variant="dark" />
            <div class="mt-6">
              <SofaxArrowLink to="/ats-check" :label="t('homepage.interviewPrep.cvLink')" />
            </div>
          </div>

          <ol class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <li v-for="(tip, index) in tips" :key="tip.id">
              <LocaleLink
                :to="tip.to"
                class="group flex h-full flex-col rounded-[0.625rem] bg-white p-[1.875rem] transition-all duration-300 hover:shadow-lift dark:bg-gray-800"
              >
                <span
                  class="mb-5 inline-flex size-10 items-center justify-center rounded-full bg-ink !text-base !font-bold !text-white transition-colors duration-300 group-hover:bg-accent dark:bg-white dark:!text-ink"
                  aria-hidden="true"
                >{{ index + 1 }}</span>
                <span class="mb-2 font-title text-xl font-bold -tracking-[0.3px] text-ink dark:text-white">
                  {{ t(`homepage.interviewPrep.tips.${tip.id}.title`) }}
                </span>
                <span class="text-base">{{ t(`homepage.interviewPrep.tips.${tip.id}.text`) }}</span>
              </LocaleLink>
            </li>
          </ol>
        </div>
      </div>
    </div>
  </section>
</template>
